interface FieldDef {
  name: string;
  appliesTo: 'both' | 'movie' | 'episode';
  required: 'yes' | 'no' | 'conditional';
  notes: string;
  example?: string;
}

const FIELDS: FieldDef[] = [
  {
    name: 'imdb_id',
    appliesTo: 'both',
    required: 'no',
    notes: 'IMDb ID. Format: tt + 5 or more digits.',
    example: 'tt0112843',
  },
  {
    name: 'provider_id',
    appliesTo: 'both',
    required: 'yes',
    notes:
      "Your unique ID for this title. Doubles as the upload subfolder name. Letters, digits, underscore, hyphen — max 64 characters.",
    example: 'the-demolitionist',
  },
  {
    name: 'series_title',
    appliesTo: 'episode',
    required: 'yes',
    notes:
      'Display title of the parent series. Episodes that share this string are grouped under one series record.',
    example: 'Graveyard Shift',
  },
  {
    name: 'season_number',
    appliesTo: 'episode',
    required: 'yes',
    notes: 'Positive integer.',
    example: '1',
  },
  {
    name: 'episode_number',
    appliesTo: 'episode',
    required: 'yes',
    notes: 'Positive integer, unique within the season.',
    example: '1',
  },
  {
    name: 'title',
    appliesTo: 'both',
    required: 'yes',
    notes: 'Official display title (movie title or episode title).',
    example: 'The Demolitionist',
  },
  {
    name: 'content_type',
    appliesTo: 'both',
    required: 'yes',
    notes: 'Either "movie" or "episode". Selects which validation rules apply to this row.',
    example: 'movie',
  },
  {
    name: 'description',
    appliesTo: 'both',
    required: 'yes',
    notes: 'Full synopsis. No length limit but keep it concise.',
  },
  {
    name: 'release_date',
    appliesTo: 'both',
    required: 'no',
    notes: '4-digit year only.',
    example: '1995',
  },
  {
    name: 'parental_rating',
    appliesTo: 'both',
    required: 'yes',
    notes: 'One of: G, PG, PG-13, R, NC-17, NR.',
    example: 'R',
  },
  {
    name: 'languages',
    appliesTo: 'both',
    required: 'no',
    notes: 'ISO 639-1 codes, comma-separated.',
    example: 'en, es',
  },
  {
    name: 'countries',
    appliesTo: 'both',
    required: 'no',
    notes: 'ISO 3166-1 alpha-2 codes, comma-separated.',
    example: 'US, CA',
  },
  {
    name: 'genres',
    appliesTo: 'both',
    required: 'yes',
    notes: 'Comma-separated values from the approved DFX genre list.',
    example: 'Action, Sci-Fi, Thriller',
  },
  { name: 'directors', appliesTo: 'both', required: 'no', notes: 'Comma-separated full names.' },
  { name: 'actors', appliesTo: 'both', required: 'no', notes: 'Comma-separated full names.' },
  { name: 'writers', appliesTo: 'both', required: 'no', notes: 'Comma-separated full names.' },
  {
    name: 'keywords',
    appliesTo: 'both',
    required: 'no',
    notes: 'Discoverability tags, comma-separated.',
    example: 'cyborg, revenge, post-apocalyptic',
  },
  {
    name: 'cue_points',
    appliesTo: 'both',
    required: 'no',
    notes:
      'Mid-roll ad break locations. Either decimal seconds or SMPTE timecode (HH:MM:SS or HH:MM:SS:FF), comma-separated.',
    example: '699.1, 1271.7',
  },
  {
    name: 'country_whitelist',
    appliesTo: 'both',
    required: 'no',
    notes:
      'ISO 3166-1 codes; if set, the title is only available in these countries. Cannot be combined with country_blacklist.',
    example: 'US, CA',
  },
  {
    name: 'country_blacklist',
    appliesTo: 'both',
    required: 'no',
    notes:
      'ISO 3166-1 codes; if set, the title is unavailable in these countries. Cannot be combined with country_whitelist.',
    example: 'CN, RU',
  },
  {
    name: 'available_date',
    appliesTo: 'both',
    required: 'no',
    notes: 'When the title may begin streaming. MM/DD/YYYY.',
    example: '06/01/2026',
  },
  {
    name: 'expiration_date',
    appliesTo: 'both',
    required: 'no',
    notes: 'When rights expire. MM/DD/YYYY.',
    example: '06/01/2031',
  },
  {
    name: 'video_filename',
    appliesTo: 'both',
    required: 'yes',
    notes:
      'Feature video file. Use whatever filename matches your existing convention — must match an uploaded file in video/ exactly (case-sensitive). See Asset specs below for resolution and codec.',
    example: 'feature.mov',
  },
  {
    name: 'trailer_filename',
    appliesTo: 'movie',
    required: 'no',
    notes:
      'Optional trailer file. Match an uploaded file in video/. Episodes do not have trailers in v1 — leave blank.',
    example: 'trailer.mov',
  },
  {
    name: 'audio_filenames',
    appliesTo: 'both',
    required: 'no',
    notes:
      'Comma-separated filename:lang pairs for alternate-language audio in audio/. Languages are ISO 639-1, no duplicates. Quote the cell in your CSV (any spreadsheet does this automatically when you type in commas).',
    example: 'feature.wav:en, feature_es.wav:es',
  },
  {
    name: 'poster_filename',
    appliesTo: 'both',
    required: 'yes',
    notes: 'Portrait key art. Match an uploaded file in artwork/. See Asset specs below.',
    example: 'poster.png',
  },
  {
    name: 'landscape_filename',
    appliesTo: 'both',
    required: 'yes',
    notes: 'Landscape key art for grids and rails. See Asset specs below.',
    example: 'landscape.png',
  },
  {
    name: 'hero_filename',
    appliesTo: 'both',
    required: 'yes',
    notes:
      'Hero / featured image for the homepage and detail pages. See Asset specs below.',
    example: 'hero.jpg',
  },
  {
    name: 'subtitle_filenames',
    appliesTo: 'both',
    required: 'no',
    notes:
      'Comma-separated filename:lang pairs for subtitle files in subtitles/. Languages are ISO 639-1, no duplicates. Use SRT, VTT, or TTML. Quote the cell in your CSV (any spreadsheet does this automatically when you type in commas).',
    example: 'captions.srt:en, captions_es.srt:es',
  },
];

const TEMPLATE_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export function SpecPage() {
  return (
    <div className="mx-auto max-w-3xl">
      <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--color-text-muted)]">
        Reference
      </div>
      <h1 className="mt-1 text-2xl font-semibold">Metadata spec</h1>
      <p className="mt-2 text-sm text-[color:var(--color-text-secondary)]">
        One CSV format covers movies and episodic series. Each row's{' '}
        <code className="font-mono">content_type</code> selects the rules: set it to{' '}
        <code className="font-mono">movie</code> for films, or{' '}
        <code className="font-mono">episode</code> for series episodes. Mix freely in one
        delivery.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <DownloadCard
          title="Metadata template"
          subtitle="Headers only — fill in your rows"
          href={`${TEMPLATE_BASE}/api/v1/templates/metadata.csv`}
        />
        <DownloadCard
          title="Worked sample"
          subtitle="1 movie + 2 episodes"
          href={`${TEMPLATE_BASE}/api/v1/templates/metadata-sample.csv`}
        />
      </div>

      <h2 className="mt-10 text-lg font-semibold">Fields</h2>
      <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
        Listed in column order. The "Applies to" column shows whether a field is used by
        movie rows, episode rows, or both. Putting a value in a field that doesn't apply
        to that row's <code className="font-mono">content_type</code> is a validation
        error, not a silent drop.
      </p>
      <FieldTable fields={FIELDS} />

      <h2 className="mt-10 text-lg font-semibold">Asset specs</h2>
      <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
        Targets for each file referenced from the metadata CSV. We don't auto-reject
        below-spec files in v1, but anything under the minimums will look poor on
        4K displays and may need to be redelivered before launch.
      </p>
      <AssetSpecTable />

      <h2 className="mt-10 text-lg font-semibold">Notes</h2>
      <ul className="mt-3 list-disc space-y-2 pl-6 text-sm text-[color:var(--color-text-secondary)]">
        <li>
          Standard CSV. Quote any cell containing a comma or newline. UTF-8.
        </li>
        <li>
          The CSV must be uploaded as <code className="font-mono">metadata.csv</code> at the
          batch root — that filename is what we look for.
        </li>
        <li>
          Asset filenames are <em>licensor-chosen</em> — keep your existing naming
          convention. The CSV's <code className="font-mono">video_filename</code>,{' '}
          <code className="font-mono">trailer_filename</code>,{' '}
          <code className="font-mono">poster_filename</code>, etc. are how we know which
          file is which. Match must be exact, case-sensitive, and the file must live in the
          appropriate subdirectory (<code className="font-mono">video/</code>,{' '}
          <code className="font-mono">artwork/</code>, etc.).
        </li>
        <li>
          For episodes, the system creates a parent series record on first ingest keyed by
          a slug of <code className="font-mono">series_title</code>. Use the same string
          across all episodes of a series so they group correctly.
        </li>
        <li>
          Approved genres: Action, Adventure, Adult, Animation, Anime, Comedy, Crime,
          Documentary, Drama, Fantasy, Foreign/International, History, Holiday, Horror,
          Independent, Kids & Family, LGBT, Lifestyle, Music, Musicals, Mystery, Reality,
          Romance, Sci-Fi, Science & Nature, Sport, Thriller, War, Western.
        </li>
      </ul>

      <p className="mt-10 text-xs text-[color:var(--color-text-secondary)]">
        Need a narrower template?{' '}
        <a className="underline" href={`${TEMPLATE_BASE}/api/v1/templates/movie-metadata.csv`}>
          movie-only
        </a>{' '}
        or{' '}
        <a className="underline" href={`${TEMPLATE_BASE}/api/v1/templates/episode-metadata.csv`}>
          episode-only
        </a>
        . Both are valid in a delivery; the unified template above is otherwise recommended.
      </p>
    </div>
  );
}

function DownloadCard({
  title,
  subtitle,
  href,
}: {
  title: string;
  subtitle: string;
  href: string;
}) {
  return (
    <a
      href={href}
      download
      className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-4 transition hover:border-[color:var(--color-crimson)]"
    >
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-1 text-xs text-[color:var(--color-text-secondary)]">{subtitle}</div>
      <div className="mt-2 text-xs text-[color:var(--color-crimson)]">Download CSV →</div>
    </a>
  );
}

function FieldTable({ fields }: { fields: FieldDef[] }) {
  return (
    <div className="mt-3 overflow-x-auto rounded border border-[color:var(--color-border)]">
      <table className="w-full text-sm">
        <thead className="bg-[color:var(--color-bg-card)] text-left text-xs uppercase tracking-wider text-[color:var(--color-text-muted)]">
          <tr>
            <th className="px-4 py-2 font-medium">Field</th>
            <th className="px-4 py-2 font-medium">Applies to</th>
            <th className="px-4 py-2 font-medium">Required</th>
            <th className="px-4 py-2 font-medium">Notes</th>
            <th className="px-4 py-2 font-medium">Example</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.name} className="border-t border-[color:var(--color-border)] align-top">
              <td className="px-4 py-3 font-mono text-xs">{f.name}</td>
              <td className="px-4 py-3 text-xs">
                <AppliesBadge value={f.appliesTo} />
              </td>
              <td className="px-4 py-3 text-xs">
                <RequiredBadge value={f.required} />
              </td>
              <td className="px-4 py-3 text-[color:var(--color-text-secondary)]">{f.notes}</td>
              <td className="px-4 py-3 font-mono text-xs">{f.example ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface AssetSpec {
  field: string;
  aspect: string;
  min: string;
  recommended: string;
  formats: string;
  notes?: string;
}

const ASSET_SPECS: AssetSpec[] = [
  {
    field: 'video_filename',
    aspect: '16:9',
    min: '1920 × 1080 (HD)',
    recommended: '3840 × 2160 (4K)',
    formats: 'ProRes 422 HQ, H.264, or HEVC in MOV / MP4',
    notes: '24, 25, or 30 fps. Stereo or 5.1 audio embedded.',
  },
  {
    field: 'trailer_filename',
    aspect: '16:9',
    min: '1920 × 1080',
    recommended: '1920 × 1080',
    formats: 'Same container/codecs as feature',
    notes: '60–120 seconds typical.',
  },
  {
    field: 'poster_filename',
    aspect: '2:3 portrait',
    min: '1000 × 1500',
    recommended: '2000 × 3000',
    formats: 'PNG (preferred) or JPEG',
    notes: 'Title treatment included. Centered, no bleed.',
  },
  {
    field: 'landscape_filename',
    aspect: '16:9',
    min: '1920 × 1080',
    recommended: '3840 × 2160',
    formats: 'PNG or JPEG',
    notes: 'No title treatment — used in grids and rails alongside the title text.',
  },
  {
    field: 'hero_filename',
    aspect: '16:9 (or 21:9 cinematic)',
    min: '1920 × 1080',
    recommended: '3840 × 2160',
    formats: 'JPEG',
    notes: 'Cinematic still. Avoid hard subjects on the right third (text overlay area).',
  },
  {
    field: 'subtitle_filename',
    aspect: '—',
    min: '—',
    recommended: '—',
    formats: 'SRT (preferred), VTT, or TTML',
    notes: 'UTF-8, no BOM. One file per language, paired with subtitle_language.',
  },
  {
    field: 'audio (per-language WAV)',
    aspect: '—',
    min: '48 kHz / 16-bit',
    recommended: '48 kHz / 24-bit',
    formats: 'WAV uncompressed',
    notes:
      'Optional alternate-language audio under audio/. Use any filename. Stereo or 5.1.',
  },
];

function AssetSpecTable() {
  return (
    <div className="mt-3 overflow-x-auto rounded border border-[color:var(--color-border)]">
      <table className="w-full text-sm">
        <thead className="bg-[color:var(--color-bg-card)] text-left text-xs uppercase tracking-wider text-[color:var(--color-text-muted)]">
          <tr>
            <th className="px-4 py-2 font-medium">Field</th>
            <th className="px-4 py-2 font-medium">Aspect</th>
            <th className="px-4 py-2 font-medium">Min</th>
            <th className="px-4 py-2 font-medium">Recommended</th>
            <th className="px-4 py-2 font-medium">Formats</th>
            <th className="px-4 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {ASSET_SPECS.map((s) => (
            <tr key={s.field} className="border-t border-[color:var(--color-border)] align-top">
              <td className="px-4 py-3 font-mono text-xs">{s.field}</td>
              <td className="px-4 py-3 text-xs">{s.aspect}</td>
              <td className="px-4 py-3 text-xs">{s.min}</td>
              <td className="px-4 py-3 text-xs">{s.recommended}</td>
              <td className="px-4 py-3 text-xs">{s.formats}</td>
              <td className="px-4 py-3 text-[color:var(--color-text-secondary)]">{s.notes ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AppliesBadge({ value }: { value: 'both' | 'movie' | 'episode' }) {
  const text = value === 'both' ? 'movie + episode' : value;
  return <span className="text-[color:var(--color-text-secondary)]">{text}</span>;
}

function RequiredBadge({ value }: { value: 'yes' | 'no' | 'conditional' }) {
  if (value === 'yes')
    return <span className="text-[color:var(--color-warning)]">required</span>;
  if (value === 'conditional')
    return <span className="text-[color:var(--color-text-secondary)]">conditional</span>;
  return <span className="text-[color:var(--color-text-secondary)]">optional</span>;
}
